/**
 * Akkoord-kaart "Klant verwijderen (AVG)" (sessie 73 blok 4). Opent vanuit
 * het menu "..." in de kop (alleen beheerder), als vlak met een kruisje.
 *
 * Stap 1: wat er gewist wordt en wat blijft (`GET
 * /api/admin/avg/voorbereiding/{id}`), e-mailadressen met kopieerknop
 * (voor Reloadify), Lightspeed-links, reden (verplicht) en twee vinkjes
 * voor wat JP buiten de werkplek al deed. Stap 2: één regel samenvatting,
 * het vinkje "juiste klant" en "Definitief wissen". De server eist beide
 * bevestigingen; niet afgevinkte stappen worden een open actie voor JP.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";

import { avgApi, vertaalApiFout } from "@/lib/api";
import type { AvgAnonimiseerUitkomst } from "@/types/api";

import { Icoon } from "../../Icoon";
import { veiligeLink } from "../../Rechts/opmaak";
import { v6Sleutels } from "../../sleutels";

/** Zelfde grens als de server (app/api/avg.py): een reden van minstens drie tekens. */
const MIN_REDEN = 3;

type Stap = "controle" | "bevestig";

/** Wat met Tab bereikbaar is binnen het vlak (voor de focus-trap). */
const FOCUSBAAR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Kopieer({ tekst, label }: { tekst: string; label?: string }) {
  const [gedaan, setGedaan] = useState<"ja" | "nee" | null>(null);
  const kopieer = async () => {
    try {
      await navigator.clipboard.writeText(tekst);
      setGedaan("ja");
    } catch {
      setGedaan("nee");
    }
  };
  return (
    <button
      type="button"
      className="kk-mini-knop"
      aria-label={label ?? `Kopieer ${tekst}`}
      onClick={() => void kopieer()}
    >
      {gedaan === "ja" ? "Gekopieerd" : gedaan === "nee" ? "Niet gelukt" : "Kopieer"}
    </button>
  );
}

export function AvgVerwijderen({
  klantId,
  onSluit,
  onGewist,
}: {
  klantId: string;
  onSluit: () => void;
  /** Na "Gewist": terug naar de lijst. */
  onGewist: () => void;
}) {
  const queryClient = useQueryClient();
  const [stap, setStap] = useState<Stap>("controle");
  const [reden, setReden] = useState("");
  const [lightspeed, setLightspeed] = useState(false);
  const [reloadify, setReloadify] = useState(false);
  const [juist, setJuist] = useState(false);
  /** Besluit JP 30-09: samengevoegde accounts alleen mee na bevestiging van de klant. */
  const [klantBevestigd, setKlantBevestigd] = useState(false);
  const [uitkomst, setUitkomst] = useState<AvgAnonimiseerUitkomst | null>(null);
  /** "Wijzigingen weggooien?" staat in beeld (na Escape of de overlay). */
  const [weggooien, setWeggooien] = useState(false);
  const vlakRef = useRef<HTMLDivElement>(null);
  const redenRef = useRef<HTMLTextAreaElement>(null);
  const terugRef = useRef<HTMLButtonElement>(null);
  const redenGefocust = useRef(false);

  const voorbereiding = useQuery({
    queryKey: ["werkplek", "avg", klantId, "voorbereiding"],
    queryFn: () => avgApi.voorbereiding(klantId),
    staleTime: 0,
  });
  const wis = useMutation({
    mutationFn: () =>
      avgApi.anonimiseer(klantId, {
        bevestigd: true,
        tweede_bevestiging: true,
        reden: reden.trim(),
        lightspeed_gedaan: lightspeed,
        reloadify_gedaan: reloadify,
        samengevoegd_mee: klantBevestigd,
        klant_bevestigd: klantBevestigd,
      }),
    onSuccess: async (uit) => {
      setUitkomst(uit);
      // De kaart blijft tot "Naar de lijst" in de cache staan: weghalen zou
      // hem opnieuw laten ophalen (404), en dan verdwijnt deze melding met
      // de open afvinkstappen. Alleen de lijsten verversen.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["v6", "klanten"] }),
        queryClient.invalidateQueries({ queryKey: v6Sleutels.lijst }),
      ]);
    },
  });

  /** Naar de lijst, daarna alles van deze klant uit de cache (kaart, tijdlijn, copiloot). */
  const naarLijst = () => {
    onGewist();
    const vanKlant = (key: readonly unknown[]) =>
      key.some((deel) =>
        typeof deel === "string"
          ? deel === klantId
          : typeof deel === "object" && deel !== null && Object.values(deel).includes(klantId),
      );
    void queryClient.cancelQueries({ predicate: (q) => vanKlant(q.queryKey) });
    queryClient.removeQueries({ predicate: (q) => vanKlant(q.queryKey) });
  };

  const bezig = wis.isPending;
  const v = voorbereiding.data;
  const al = Boolean(v?.al_geanonimiseerd);
  /** Is er iets ingevuld dat met sluiten verloren gaat? */
  const ingevuld =
    !uitkomst && (reden.trim() !== "" || lightspeed || reloadify || juist || klantBevestigd);

  // Escape of de overlay: sluiten, maar niet zonder meer als er al iets
  // is ingevuld. Dan eerst "Wijzigingen weggooien?". Niet tijdens het wissen.
  const vraagSluiten = () => {
    if (bezig) return;
    if (ingevuld) setWeggooien(true);
    else onSluit();
  };
  const vraagSluitenRef = useRef(vraagSluiten);
  useEffect(() => {
    vraagSluitenRef.current = vraagSluiten;
  });
  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (weggooien) setWeggooien(false);
      else vraagSluitenRef.current();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [weggooien]);

  // Startfocus: het vlak zelf tot de voorbereiding er is, daarna één keer
  // het redenveld.
  useEffect(() => {
    vlakRef.current?.focus();
  }, []);
  useEffect(() => {
    if (redenGefocust.current || !redenRef.current) return;
    redenGefocust.current = true;
    redenRef.current.focus();
  }, [v, stap]);
  useEffect(() => {
    if (weggooien) terugRef.current?.focus();
  }, [weggooien]);

  // Focus-trap: Tab en Shift+Tab blijven binnen het vlak.
  const houdFocus = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !vlakRef.current) return;
    const focusbaar = Array.from(vlakRef.current.querySelectorAll<HTMLElement>(FOCUSBAAR));
    if (focusbaar.length === 0) {
      e.preventDefault();
      return;
    }
    const eerste = focusbaar[0];
    const laatste = focusbaar[focusbaar.length - 1];
    const actief = document.activeElement;
    const binnen = actief instanceof Node && vlakRef.current.contains(actief);
    if (e.shiftKey && (actief === eerste || actief === vlakRef.current || !binnen)) {
      e.preventDefault();
      laatste.focus();
    } else if (!e.shiftKey && (actief === laatste || !binnen)) {
      e.preventDefault();
      eerste.focus();
    }
  };

  const naam = v?.naam || "Deze klant";
  const samen = v?.samengevoegde_accounts ?? [];
  const open = [!lightspeed ? "Lightspeed" : null, !reloadify ? "Reloadify" : null].filter(
    Boolean,
  );

  let inhoud;
  let voet;
  if (uitkomst) {
    inhoud = (
      <div className="kk-avg-klaar" role="status">
        <b>Gewist</b>
        {uitkomst.ook_gewist && uitkomst.ook_gewist.length > 0 ? (
          <span>
            Ook gewist:{" "}
            {uitkomst.ook_gewist.length === 1
              ? "1 samengevoegd account"
              : `${uitkomst.ook_gewist.length} samengevoegde accounts`}
          </span>
        ) : null}
        {uitkomst.afvinkstappen_open.length > 0 ? (
          <>
            <span>Nog te doen, staat als actie voor JP:</span>
            <ul>
              {uitkomst.afvinkstappen_open.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    );
    voet = (
      <button type="button" className="v6-knop acc" onClick={naarLijst}>
        Naar de lijst
      </button>
    );
  } else if (voorbereiding.isLoading) {
    inhoud = <p className="stil">Laden</p>;
    voet = null;
  } else if (voorbereiding.isError || !v) {
    inhoud = (
      <p className="v6-fout" role="alert">
        {vertaalApiFout(voorbereiding.error, "Voorbereiding niet geladen.")}
      </p>
    );
    voet = null;
  } else if (stap === "controle") {
    const links = v.lightspeed_urls.map(veiligeLink).filter((u): u is string => Boolean(u));
    inhoud = (
      <>
        <div className="kk-avg-rij">
          <small>Klant</small>
          <b>{naam}</b>
        </div>
        <div className="kk-avg-rij">
          <small>E-mail</small>
          <div className="kk-avg-lijst">
            {v.emails.length === 0 ? (
              <span className="stil">geen</span>
            ) : (
              v.emails.map((e) => (
                <span key={e} className="kk-avg-email">
                  <span>{e}</span>
                  <Kopieer tekst={e} />
                </span>
              ))
            )}
          </div>
        </div>
        <div className="kk-avg-rij">
          <small>Lightspeed</small>
          <div className="kk-avg-lijst">
            {links.length === 0 ? (
              <span className="stil">geen</span>
            ) : (
              links.map((u, i) => (
                <a key={u} href={u} target="_blank" rel="noopener noreferrer">
                  {`Account ${i + 1}`} <Icoon naam="uit" klein />
                </a>
              ))
            )}
          </div>
        </div>
        <div className="kk-avg-twee">
          <div>
            <small>Wordt gewist</small>
            <ul data-testid="avg-wordt-gewist">
              {v.wordt_gewist.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
          <div>
            <small>Blijft</small>
            <ul data-testid="avg-blijft">
              {v.blijft.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        </div>
        {al ? (
          <p className="kk-avg-al" role="status">
            Al gewist
          </p>
        ) : (
          <>
            <label className="kk-veld">
              <span className="l">Reden</span>
              <textarea
                ref={redenRef}
                rows={2}
                required
                value={reden}
                onChange={(e) => setReden(e.target.value)}
              />
            </label>
            <label className="kk-vink">
              <input
                type="checkbox"
                checked={lightspeed}
                onChange={(e) => setLightspeed(e.target.checked)}
              />
              Gewist in Lightspeed
            </label>
            <label className="kk-vink">
              <input
                type="checkbox"
                checked={reloadify}
                onChange={(e) => setReloadify(e.target.checked)}
              />
              Uitgeschreven in Reloadify
            </label>
            <small className="stil">Niet aangevinkt wordt een open actie voor JP.</small>
            {samen.length > 0 ? (
              <div className="kk-avg-samengevoegd" data-testid="avg-samengevoegd">
                <small>
                  {samen.length === 1
                    ? "1 samengevoegd account"
                    : `${samen.length} samengevoegde accounts`}
                </small>
                <ul>
                  {samen.map((a) => (
                    <li key={a.klant_id}>
                      {a.email ?? "zonder e-mailadres"}
                      {a.lightspeed_url ? (
                        <>
                          {" "}
                          <a href={a.lightspeed_url} target="_blank" rel="noopener noreferrer">
                            Lightspeed
                          </a>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {v.bericht_aan_klant ? (
                  <>
                    <small>Vraag de klant eerst</small>
                    <p className="kk-avg-bericht">{v.bericht_aan_klant}</p>
                    <Kopieer tekst={v.bericht_aan_klant} label="Kopieer bericht aan de klant" />
                  </>
                ) : null}
                <label className="kk-vink">
                  <input
                    type="checkbox"
                    checked={klantBevestigd}
                    onChange={(e) => setKlantBevestigd(e.target.checked)}
                  />
                  Klant heeft bevestigd dat deze accounts van hem zijn; mee wissen
                </label>
              </div>
            ) : null}
          </>
        )}
      </>
    );
    voet = (
      <button
        type="button"
        className="v6-knop acc"
        disabled={al || reden.trim().length < MIN_REDEN}
        onClick={() => setStap("bevestig")}
      >
        Verder
      </button>
    );
  } else {
    const samenvatting = [
      `${naam}: ${v.wordt_gewist.length} onderdelen wissen`,
      samen.length > 0
        ? klantBevestigd
          ? `plus ${samen.length === 1 ? "1 samengevoegd account" : `${samen.length} samengevoegde accounts`}`
          : `${samen.length === 1 ? "1 samengevoegd account blijft" : `${samen.length} samengevoegde accounts blijven`}`
        : null,
      `reden "${reden.trim()}"`,
      open.length > 0 ? `open voor JP: ${open.join(" en ")}` : null,
    ]
      .filter(Boolean)
      .join(", ");
    inhoud = (
      <>
        <p className="kk-avg-samen" data-testid="avg-samenvatting">
          {samenvatting}
        </p>
        <label className="kk-vink">
          <input type="checkbox" checked={juist} onChange={(e) => setJuist(e.target.checked)} />
          Ik heb gecontroleerd dat dit de juiste klant is
        </label>
        {wis.isError ? (
          <p className="v6-fout" role="alert">
            {vertaalApiFout(wis.error, "Wissen lukte niet.")}
          </p>
        ) : null}
      </>
    );
    voet = (
      <>
        <button
          type="button"
          className="v6-knop"
          disabled={bezig}
          onClick={() => {
            setStap("controle");
            setJuist(false);
            wis.reset();
          }}
        >
          Terug
        </button>
        <button
          type="button"
          className="v6-knop kk-wis"
          disabled={!juist || bezig || al}
          onClick={() => wis.mutate()}
        >
          Definitief wissen
        </button>
      </>
    );
  }

  return (
    <>
      <div className="kk-vlak-overlay" data-testid="avg-overlay" onClick={vraagSluiten} />
      <div
        ref={vlakRef}
        className="kk-vlak kk-avg"
        role="dialog"
        aria-modal="true"
        aria-label="Klant verwijderen (AVG)"
        tabIndex={-1}
        onKeyDown={houdFocus}
      >
        <div className="kk-vlak-kop">
          <b>Klant verwijderen (AVG)</b>
          <button
            type="button"
            className="kk-kruis"
            aria-label="Sluiten"
            title="Sluiten"
            disabled={bezig}
            onClick={onSluit}
          >
            <Icoon naam="x" klein />
          </button>
        </div>
        <div className="kk-vlak-inhoud">{inhoud}</div>
        {weggooien ? (
          <div className="kk-vlak-voet" role="group" aria-label="Wijzigingen weggooien?">
            <span>Wijzigingen weggooien?</span>
            <button type="button" className="v6-knop kk-wis" onClick={onSluit}>
              Weggooien
            </button>
            <button
              ref={terugRef}
              type="button"
              className="v6-knop"
              onClick={() => setWeggooien(false)}
            >
              Terug
            </button>
          </div>
        ) : voet ? (
          <div className="kk-vlak-voet">{voet}</div>
        ) : null}
      </div>
    </>
  );
}
